import React, { useState } from "react";
import { CalendarBlank, X } from "@phosphor-icons/react";

const iso = (d) => d.toISOString().slice(0, 10);

function today() { return new Date(); }
function daysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d; }
function startOfMonth() { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); }
function startOfYear() { const d = new Date(); return new Date(d.getFullYear(), 0, 1); }

const PRESETS = [
  { key: "all", label: "Tudo", range: () => ({ from: "", to: "" }) },
  { key: "7d", label: "Últimos 7d", range: () => ({ from: iso(daysAgo(6)), to: iso(today()) }) },
  { key: "30d", label: "Últimos 30d", range: () => ({ from: iso(daysAgo(29)), to: iso(today()) }) },
  { key: "90d", label: "Últimos 90d", range: () => ({ from: iso(daysAgo(89)), to: iso(today()) }) },
  { key: "month", label: "Este mês", range: () => ({ from: iso(startOfMonth()), to: iso(today()) }) },
  { key: "year", label: "Este ano", range: () => ({ from: iso(startOfYear()), to: iso(today()) }) },
];

/**
 * PeriodFilter — controlled component.
 * value = { from: 'YYYY-MM-DD' | '', to: 'YYYY-MM-DD' | '' }
 * onChange(newValue)
 */
export default function PeriodFilter({ value, onChange, testid = "period-filter" }) {
  const [showCustom, setShowCustom] = useState(false);
  const active = detectActivePreset(value);

  const pick = (preset) => {
    setShowCustom(false);
    onChange(preset.range());
  };

  return (
    <div data-testid={testid} className="card-brutal p-4 bg-brand-yellow">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarBlank weight="bold" size={16} />
          <span className="label-brutal !mb-0">Período</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {PRESETS.map((p) => (
            <Chip
              key={p.key}
              active={active === p.key && !showCustom}
              onClick={() => pick(p)}
              testid={`${testid}-${p.key}`}
            >
              {p.label}
            </Chip>
          ))}
          <Chip
            active={showCustom || active === "custom"}
            onClick={() => setShowCustom((s) => !s)}
            testid={`${testid}-custom`}
          >
            Personalizado
          </Chip>
        </div>
        {value.from || value.to ? (
          <div className="ml-auto flex items-center gap-2 text-xs font-bold text-brand-ink2">
            <span>{value.from || "início"} → {value.to || "hoje"}</span>
            <button
              type="button"
              data-testid={`${testid}-clear`}
              onClick={() => { setShowCustom(false); onChange({ from: "", to: "" }); }}
              className="p-1 border-2 border-brand-ink bg-white hover:bg-brand-peach"
              title="Limpar"
            >
              <X weight="bold" size={12} />
            </button>
          </div>
        ) : null}
      </div>

      {showCustom && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
          <div>
            <label className="label-brutal">De</label>
            <input
              type="date"
              data-testid={`${testid}-from`}
              className="input-brutal"
              value={value.from || ""}
              onChange={(e) => onChange({ ...value, from: e.target.value })}
            />
          </div>
          <div>
            <label className="label-brutal">Até</label>
            <input
              type="date"
              data-testid={`${testid}-to`}
              className="input-brutal"
              value={value.to || ""}
              onChange={(e) => onChange({ ...value, to: e.target.value })}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 border-2 border-brand-ink text-xs font-bold uppercase tracking-wide transition-all ${
        active ? "bg-brand-pink text-white shadow-brutalSm" : "bg-white hover:bg-brand-yellow"
      }`}
    >
      {children}
    </button>
  );
}

function detectActivePreset({ from, to }) {
  if (!from && !to) return "all";
  const now = iso(today());
  if (to !== now) return "custom";
  const p7 = iso(daysAgo(6));
  const p30 = iso(daysAgo(29));
  const p90 = iso(daysAgo(89));
  const pm = iso(startOfMonth());
  const py = iso(startOfYear());
  if (from === p7) return "7d";
  if (from === p30) return "30d";
  if (from === p90) return "90d";
  if (from === pm) return "month";
  if (from === py) return "year";
  return "custom";
}

/** Build query string from filter value. Returns '' if all empty. */
export const toQuery = (v) => {
  const params = new URLSearchParams();
  if (v?.from) params.set("date_from", v.from);
  if (v?.to) params.set("date_to", v.to);
  const s = params.toString();
  return s ? `?${s}` : "";
};
